/**
 * Webhooks & API Keys section for managing API keys and viewing webhook documentation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy01, Key01, Trash01, XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type {
  CreateTeamApiKeyResponse,
  TeamApiKey,
} from '@/lib/api/teamApiKeys';
import {
  createTeamApiKey,
  deleteTeamApiKey,
  listTeamApiKeys,
} from '@/lib/api/teamApiKeys';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { getApiBaseUrl } from '@/lib/config';

const PAYLOAD_EXAMPLE_NOTE = `{
  "contact": {
    "email": "john@example.com",
    "first_name": "John",
    "last_name": "Doe",
    "company": "Acme Corp",
    "title": "CEO",
    "phone": "+1234567890",
    "linkedin_url": "https://linkedin.com/in/johndoe"
  },
  "timeline_event": {
    "type": "note",
    "content": "Met at conference, interested in demo"
  },
  "idempotency_key": "unique-key-123"
}`;

const PAYLOAD_EXAMPLE_LINKEDIN = `{
  "contact": {
    "email": "john@example.com",
    "linkedin_url": "https://linkedin.com/in/johndoe"
  },
  "timeline_event": {
    "type": "linkedin_message",
    "direction": "received",
    "content": "Thanks for connecting!",
    "linkedin_thread_id": "urn:li:messagingThread:2-ZmY5...",
    "timestamp": "2024-01-15T10:30:00Z"
  }
}`;

const PAYLOAD_EXAMPLE_PROPERTIES = `{
  "contact": {
    "email": "john@example.com",
    "first_name": "John"
  },
  "properties": {
    "ahoy_location": "Dublin",
    "org_custom_tier": "gold",
    "org_custom_tags": ["enterprise", "trial"]
  },
  "write_mode": "overwrite",
  "idempotency_key": "unique-key-456"
}`;

const RESPONSE_EXAMPLE = `{
  "status": "success",
  "contact_entity_id": "550e8400-e29b-41d4-a716-446655440000",
  "contact_created": true,
  "events_processed": 2
}`;

const ERROR_RESPONSE_EXAMPLE = `{
  "error": "Validation failed for 2 event(s)",
  "error_type": "validation_error",
  "validation_errors": [
    {
      "code": "field_collision",
      "fields": ["contact.email", "properties.ahoy_email"],
      "message": "Both 'contact.email' and 'properties.ahoy_email' were provided; each logical field may appear in only one place."
    },
    {
      "code": "unknown_property",
      "property": "custom_unknown_field",
      "message": "Unknown property 'custom_unknown_field' on ahoy_contact"
    }
  ]
}`;

type CopyTarget = 'generic' | 'payload' | 'response' | string;

export function WebhooksApiKeysSection() {
  const isEnabled = useFeatureFlag('inbound_webhooks');
  if (!isEnabled) {
    return null;
  }
  return <WebhooksApiKeysSectionInner />;
}

interface ApiKeyRowProps {
  apiKey: TeamApiKey;
  onInitiateDelete: (key: TeamApiKey) => void;
}

function ApiKeyRow({ apiKey, onInitiateDelete }: ApiKeyRowProps) {
  const handleDeleteClick = useCallback(() => {
    onInitiateDelete(apiKey);
  }, [onInitiateDelete, apiKey]);

  return (
    <tr className='bg-bg-primary'>
      <td className='px-4 py-3 text-primary font-medium'>{apiKey.name}</td>
      <td className='px-4 py-3 font-mono text-tertiary'>
        {apiKey.prefix}••••••••
      </td>
      <td className='px-4 py-3 text-tertiary'>
        {new Date(apiKey.created).toLocaleDateString()}
      </td>
      <td className='px-4 py-3 text-tertiary'>{apiKey.created_by_email}</td>
      <td className='px-4 py-3 text-right'>
        <Button
          color='secondary-destructive'
          size='sm'
          iconLeading={Trash01}
          onClick={handleDeleteClick}
          title='Delete key'
        />
      </td>
    </tr>
  );
}

function WebhooksApiKeysSectionInner() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const queryClient = useQueryClient();

  const [isExpanded, setIsExpanded] = useState(false);
  const [newApiKey, setNewApiKey] = useState<CreateTeamApiKeyResponse | null>(
    null
  );
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreatingMode, setIsCreatingMode] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<TeamApiKey | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<CopyTarget | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const copyTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const newKeyTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const {
    data: apiKeys = [],
    isLoading: isLoadingKeys,
    isError: isKeysError,
  } = useQuery<TeamApiKey[]>({
    queryKey: ['teamApiKeys', orgId],
    queryFn: ({ signal }) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return listTeamApiKeys(orgId, signal);
    },
    enabled: !!orgId,
  });

  const createKeyMutation = useMutation({
    mutationFn: (name: string) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return createTeamApiKey(orgId, name);
    },
    onSuccess: data => {
      setNewApiKey(data);
      setNewKeyName('');
      setIsCreatingMode(false);
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
    },
    onError: () => {
      setCreateError("We couldn't create this API key. Try again.");
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: (keyId: string) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return deleteTeamApiKey(orgId, keyId);
    },
    onSuccess: () => {
      setKeyToDelete(null);
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
    },
    onError: () => {
      setDeleteError("We couldn't delete this API key. Try again.");
    },
  });

  useEffect(() => {
    const copyRef = copyTimeoutRef;
    const newKeyRef = newKeyTimeoutRef;
    return () => {
      clearTimeout(copyRef.current);
      clearTimeout(newKeyRef.current);
    };
  }, []);

  const apiBase = getApiBaseUrl();
  const origin = apiBase || window.location.origin;
  const genericWebhookUrl = orgId
    ? `${origin}/api/orgs/${orgId}/webhooks/v1/generic/`
    : '';

  const handleCopy = useCallback(async (text: string, target: CopyTarget) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTarget(target);
      setCopyError(null);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedTarget(null);
      }, 2000);
    } catch {
      setCopyError('Copy failed – please select and copy manually.');
    }
  }, []);

  const handleCopyNewKey = useCallback(async () => {
    if (newApiKey) {
      await handleCopy(newApiKey.key, 'new-key');
    }
  }, [newApiKey, handleCopy]);

  const handleCreateSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const name = newKeyName.trim();
      if (!name) {
        return;
      }
      setCreateError(null);
      createKeyMutation.mutate(name);
    },
    [newKeyName, createKeyMutation]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (keyToDelete) {
      setDeleteError(null);
      deleteKeyMutation.mutate(keyToDelete.id);
    }
  }, [keyToDelete, deleteKeyMutation]);

  const handleDeleteCancel = useCallback(() => setKeyToDelete(null), []);

  const handleClearNewApiKey = useCallback(() => setNewApiKey(null), []);
  const handleClearCopyError = useCallback(() => setCopyError(null), []);
  const handleStartCreating = useCallback(() => {
    setIsCreatingMode(true);
    setCreateError(null);
  }, []);
  const handleCancelCreating = useCallback(() => {
    setIsCreatingMode(false);
    setNewKeyName('');
    setCreateError(null);
  }, []);
  const handleClearCreateError = useCallback(() => setCreateError(null), []);
  const handleClearDeleteError = useCallback(() => setDeleteError(null), []);
  const handleInitiateDelete = useCallback((key: TeamApiKey) => {
    setKeyToDelete(key);
    setDeleteError(null);
  }, []);

  const handleCopyGenericUrl = useCallback(
    () => handleCopy(genericWebhookUrl, 'generic'),
    [handleCopy, genericWebhookUrl]
  );
  const handleCopyPayloadNote = useCallback(
    () => handleCopy(PAYLOAD_EXAMPLE_NOTE, 'payload-note'),
    [handleCopy]
  );
  const handleCopyPayloadLinkedin = useCallback(
    () => handleCopy(PAYLOAD_EXAMPLE_LINKEDIN, 'payload-linkedin'),
    [handleCopy]
  );
  const handleCopyPayloadProperties = useCallback(
    () => handleCopy(PAYLOAD_EXAMPLE_PROPERTIES, 'payload-properties'),
    [handleCopy]
  );
  const handleCopyResponse = useCallback(
    () => handleCopy(RESPONSE_EXAMPLE, 'response'),
    [handleCopy]
  );
  const handleCopyErrorResponse = useCallback(
    () => handleCopy(ERROR_RESPONSE_EXAMPLE, 'error'),
    [handleCopy]
  );

  return (
    <AccordionItem
      isExpanded={isExpanded}
      onExpandedChange={setIsExpanded}
      className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'
    >
      <AccordionTrigger className='w-full px-6 py-5 border-b border-border-secondary text-left'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon icon={Key01} size='sm' theme='modern' color='gray' />
          <div className='flex-1'>
            <h2 className='text-lg font-semibold text-secondary'>
              Webhooks &amp; API Keys
            </h2>
            <p className='text-sm text-tertiary mt-0.5'>
              Create API keys and configure webhooks for external integrations
            </p>
          </div>
          {isLoadingKeys ? null : (
            <span className='text-tertiary'>
              <AccordionChevron variant='swap' className='w-5 h-5' />
            </span>
          )}
        </div>
      </AccordionTrigger>

      <AccordionPanel className='p-6 space-y-8'>
        {/* New Key Banner */}
        {newApiKey && (
          <div className='bg-utility-warning-50 p-4 rounded-lg border border-utility-warning-200'>
            <div className='flex items-start gap-3'>
              <Key01 className='w-5 h-5 text-utility-warning-600 mt-0.5 shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-warning-900'>
                  Save this key – you won't see it again
                </p>
                <p className='text-xs text-utility-warning-700 mt-0.5'>
                  {newApiKey.name}
                </p>
                <div className='mt-2 flex items-center gap-2'>
                  <div className='flex-1 bg-white rounded-lg px-3 py-2 font-mono text-sm text-utility-warning-900 border border-utility-warning-300 overflow-x-auto'>
                    {newApiKey.key}
                  </div>
                  <Button
                    color='secondary'
                    size='sm'
                    iconLeading={copiedTarget === 'new-key' ? Check : Copy01}
                    onClick={handleCopyNewKey}
                    title='Copy API key'
                  />
                </div>
              </div>
              <button
                type='button'
                onClick={handleClearNewApiKey}
                className='flex-shrink-0 text-utility-warning-600 hover:text-utility-warning-700'
                aria-label='Dismiss'
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Copy error */}
        {copyError && (
          <div className='bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
            <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
            <p className='text-sm text-utility-error-700'>{copyError}</p>
            <button
              type='button'
              onClick={handleClearCopyError}
              className='ml-auto text-utility-error-600 hover:text-utility-error-700'
              aria-label='Dismiss'
            >
              <XCircle className='w-4 h-4' />
            </button>
          </div>
        )}

        {/* API Keys Management */}
        <section>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <h3 className='text-sm font-semibold text-secondary'>API Keys</h3>
              <p className='text-xs text-tertiary mt-0.5'>
                Use these keys to authenticate webhook requests
              </p>
            </div>
            {!isCreatingMode && (
              <Button color='primary' size='sm' onClick={handleStartCreating}>
                Create API Key
              </Button>
            )}
          </div>

          {/* Inline create form */}
          {isCreatingMode && (
            <form
              onSubmit={handleCreateSubmit}
              className='mb-4 flex items-center gap-2'
            >
              <input
                type='text'
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder='Key name (e.g. My Integration)'
                className='flex-1 border border-border-secondary rounded-lg px-3 py-2 text-sm bg-bg-primary text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-v2-primary)]'
                autoFocus
                maxLength={100}
              />
              <Button
                type='submit'
                color='primary'
                size='sm'
                isLoading={createKeyMutation.isPending}
                isDisabled={!newKeyName.trim()}
              >
                Create
              </Button>
              <Button
                type='button'
                color='secondary'
                size='sm'
                onClick={handleCancelCreating}
                isDisabled={createKeyMutation.isPending}
              >
                Cancel
              </Button>
            </form>
          )}

          {/* Create error */}
          {createError && (
            <div className='mb-3 bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
              <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
              <p className='text-sm text-utility-error-700'>{createError}</p>
              <button
                type='button'
                onClick={handleClearCreateError}
                className='ml-auto text-utility-error-600 hover:text-utility-error-700'
                aria-label='Dismiss'
              >
                <XCircle className='w-4 h-4' />
              </button>
            </div>
          )}

          {/* Delete error */}
          {deleteError && (
            <div className='mb-3 bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
              <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
              <p className='text-sm text-utility-error-700'>{deleteError}</p>
              <button
                type='button'
                onClick={handleClearDeleteError}
                className='ml-auto text-utility-error-600 hover:text-utility-error-700'
                aria-label='Dismiss'
              >
                <XCircle className='w-4 h-4' />
              </button>
            </div>
          )}

          {/* Keys table */}
          {isLoadingKeys && (
            <p className='text-sm text-tertiary'>Loading API keys…</p>
          )}
          {!isLoadingKeys && isKeysError && (
            <div className='bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
              <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
              <p className='text-sm text-utility-error-700'>
                We couldn't load API keys. Try again later.
              </p>
            </div>
          )}
          {!isLoadingKeys && !isKeysError && apiKeys.length === 0 && (
            <p className='text-sm text-tertiary'>
              No API keys yet. Create one to get started.
            </p>
          )}
          {!isLoadingKeys && apiKeys.length > 0 && (
            <div className='border border-border-secondary rounded-lg overflow-hidden'>
              <table className='w-full text-sm'>
                <thead className='bg-bg-secondary'>
                  <tr>
                    <th className='text-left px-4 py-3 font-medium text-tertiary'>
                      Name
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-tertiary'>
                      Key Prefix
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-tertiary'>
                      Created
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-tertiary'>
                      Created By
                    </th>
                    <th className='px-4 py-3' />
                  </tr>
                </thead>
                <tbody className='divide-y divide-border-secondary'>
                  {apiKeys.map(key => (
                    <ApiKeyRow
                      key={key.id}
                      apiKey={key}
                      onInitiateDelete={handleInitiateDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Webhook Documentation */}
        <section className='space-y-5'>
          <div>
            <h3 className='text-sm font-semibold text-secondary mb-1'>
              Webhook Endpoints
            </h3>
            <p className='text-xs text-tertiary'>
              POST to these URLs from your external service
            </p>
          </div>

          {/* Generic endpoint */}
          <div>
            <p className='text-xs font-medium text-tertiary mb-1.5'>Generic</p>
            <div className='flex items-center gap-2'>
              <div className='flex-1 bg-bg-secondary rounded-lg px-3 py-2 font-mono text-sm text-tertiary border border-border-secondary overflow-x-auto'>
                {genericWebhookUrl}
              </div>
              <Button
                color='secondary'
                size='sm'
                iconLeading={copiedTarget === 'generic' ? Check : Copy01}
                onClick={handleCopyGenericUrl}
                title='Copy URL'
              />
            </div>
          </div>

          {/* Authentication */}
          <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
            <h4 className='text-sm font-semibold text-secondary mb-2'>
              Authentication
            </h4>
            <p className='text-xs text-tertiary mb-3'>
              Include your API key in one of these request headers:
            </p>
            <div className='space-y-2 font-mono text-xs'>
              <div className='bg-bg-primary rounded px-3 py-2 border border-border-secondary text-tertiary'>
                X-Api-Key: your-api-key
              </div>
              <div className='text-tertiary text-center text-xs'>or</div>
              <div className='bg-bg-primary rounded px-3 py-2 border border-border-secondary text-tertiary'>
                Authorization: Api-Key your-api-key
              </div>
            </div>
          </div>

          {/* Payload documentation */}
          <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
            <h4 className='text-sm font-semibold text-secondary mb-3'>
              Payload Structure
            </h4>
            <div className='space-y-3 text-xs text-tertiary'>
              <div>
                <p className='font-medium text-secondary mb-1'>
                  contact (required)
                </p>
                <p className='mb-1'>
                  Must include either{' '}
                  <code className='bg-bg-primary px-1 rounded'>email</code> or{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    linkedin_url
                  </code>{' '}
                  for matching. If a contact with the same email or LinkedIn URL
                  already exists, it will be updated with any new field values
                  (empty fields are not overwritten).
                </p>
                <p>
                  Optional fields:{' '}
                  <code className='bg-bg-primary px-1 rounded'>first_name</code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>last_name</code>,{' '}
                  <code className='bg-bg-primary px-1 rounded'>company</code>,{' '}
                  <code className='bg-bg-primary px-1 rounded'>title</code>,{' '}
                  <code className='bg-bg-primary px-1 rounded'>phone</code>
                </p>
              </div>
              <div>
                <p className='font-medium text-secondary mb-1'>
                  timeline_event (optional)
                </p>
                <p className='mb-1'>
                  Creates a timeline event on the contact. Requires{' '}
                  <code className='bg-bg-primary px-1 rounded'>type</code> and{' '}
                  <code className='bg-bg-primary px-1 rounded'>content</code>{' '}
                  fields.
                </p>
                <p className='mb-2'>Supported types:</p>
                <ul className='list-disc list-inside space-y-2 ml-2'>
                  <li>
                    <code className='bg-bg-primary px-1 rounded'>note</code> –
                    creates a note attached to the contact
                  </li>
                  <li>
                    <code className='bg-bg-primary px-1 rounded'>
                      linkedin_message
                    </code>{' '}
                    – creates a LinkedIn message event. Additional required
                    fields:{' '}
                    <code className='bg-bg-primary px-1 rounded'>
                      direction
                    </code>{' '}
                    (<code className='bg-bg-primary px-1 rounded'>sent</code> or{' '}
                    <code className='bg-bg-primary px-1 rounded'>received</code>
                    ),{' '}
                    <code className='bg-bg-primary px-1 rounded'>
                      linkedin_thread_id
                    </code>
                    . Optional:{' '}
                    <code className='bg-bg-primary px-1 rounded'>
                      timestamp
                    </code>{' '}
                    (ISO 8601 format)
                  </li>
                </ul>
              </div>
              <div>
                <p className='font-medium text-secondary mb-1'>
                  properties (optional)
                </p>
                <p className='mb-1'>
                  A dict of{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    {'{property_name: value}'}
                  </code>{' '}
                  pairs that set system or custom properties on the contact. Up
                  to 200 keys per request; exceeding this limit returns a{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    properties_limit_exceeded
                  </code>{' '}
                  error.
                </p>
                <p className='mb-1'>
                  Values may be scalar or a list (for multi-value properties).
                  With{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    write_mode: &quot;overwrite&quot;
                  </code>
                  , sending an empty list clears the property.
                </p>
                <p className='mb-1'>
                  Only properties with{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    value_write_access=&quot;all&quot;
                  </code>{' '}
                  are writable. Read-only properties are rejected with{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    readonly_property
                  </code>
                  ; unknown names with{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    unknown_property
                  </code>
                  .
                </p>
                <p className='mb-1'>
                  <code className='bg-bg-primary px-1 rounded'>ahoy_email</code>{' '}
                  and{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    ahoy_linkedin_url
                  </code>{' '}
                  may be provided here as a fallback identifier when{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.email
                  </code>{' '}
                  /{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.linkedin_url
                  </code>{' '}
                  are absent. At least one identifier across both blocks is
                  still required.
                </p>
                <p>
                  You cannot supply both a{' '}
                  <code className='bg-bg-primary px-1 rounded'>contact.*</code>{' '}
                  shortcut and its corresponding{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    properties.*
                  </code>{' '}
                  entry – this returns a{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    field_collision
                  </code>{' '}
                  error. Collision pairs:{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.email
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>ahoy_email</code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.linkedin_url
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    ahoy_linkedin_url
                  </code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.first_name
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    ahoy_first_name
                  </code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.last_name
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    ahoy_last_name
                  </code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.title
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    ahoy_job_title
                  </code>
                  ,{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.phone
                  </code>{' '}
                  ↔{' '}
                  <code className='bg-bg-primary px-1 rounded'>ahoy_phone</code>
                  . Note:{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    contact.company
                  </code>{' '}
                  is not in this list – it routes to an association rather than
                  a property.
                </p>
              </div>
              <div>
                <p className='font-medium text-secondary mb-1'>
                  write_mode (optional)
                </p>
                <p className='mb-1'>
                  Controls how{' '}
                  <code className='bg-bg-primary px-1 rounded'>properties</code>{' '}
                  block entries are written. Default:{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    update_if_empty
                  </code>
                  . An invalid value returns{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    invalid_write_mode
                  </code>
                  .
                </p>
                <ul className='list-disc list-inside space-y-1 ml-2'>
                  <li>
                    <code className='bg-bg-primary px-1 rounded'>
                      update_if_empty
                    </code>{' '}
                    – skip a property if the contact already has a non-empty
                    value for it
                  </li>
                  <li>
                    <code className='bg-bg-primary px-1 rounded'>
                      overwrite
                    </code>{' '}
                    – always write; for multi-value properties this replaces the
                    full list (empty list clears the property)
                  </li>
                </ul>
                <p className='mt-1'>
                  <strong>Note:</strong>{' '}
                  <code className='bg-bg-primary px-1 rounded'>write_mode</code>{' '}
                  applies only to the{' '}
                  <code className='bg-bg-primary px-1 rounded'>properties</code>{' '}
                  block.{' '}
                  <code className='bg-bg-primary px-1 rounded'>contact.*</code>{' '}
                  shortcut fields always use{' '}
                  <code className='bg-bg-primary px-1 rounded'>
                    update_if_empty
                  </code>{' '}
                  semantics regardless of this setting.
                </p>
              </div>
              <div>
                <p className='font-medium text-secondary mb-1'>
                  idempotency_key (optional)
                </p>
                <p>
                  Unique key to prevent duplicate processing. If the same key is
                  sent twice, the second request is ignored. Auto-generated from
                  contact identifier if not provided.
                </p>
              </div>
            </div>
          </div>

          {/* Payload examples */}
          <div>
            <div className='flex items-center justify-between mb-1.5'>
              <p className='text-xs font-medium text-tertiary'>
                Example: Create contact with note
              </p>
              <Button
                color='secondary'
                size='sm'
                iconLeading={copiedTarget === 'payload-note' ? Check : Copy01}
                onClick={handleCopyPayloadNote}
                title='Copy payload'
              />
            </div>
            <pre className='bg-bg-secondary rounded-lg px-4 py-3 border border-border-secondary text-xs font-mono text-tertiary overflow-x-auto whitespace-pre'>
              {PAYLOAD_EXAMPLE_NOTE}
            </pre>
          </div>

          <div>
            <div className='flex items-center justify-between mb-1.5'>
              <p className='text-xs font-medium text-tertiary'>
                Example: Create contact with LinkedIn message
              </p>
              <Button
                color='secondary'
                size='sm'
                iconLeading={
                  copiedTarget === 'payload-linkedin' ? Check : Copy01
                }
                onClick={handleCopyPayloadLinkedin}
                title='Copy payload'
              />
            </div>
            <pre className='bg-bg-secondary rounded-lg px-4 py-3 border border-border-secondary text-xs font-mono text-tertiary overflow-x-auto whitespace-pre'>
              {PAYLOAD_EXAMPLE_LINKEDIN}
            </pre>
          </div>

          <div>
            <div className='flex items-center justify-between mb-1.5'>
              <p className='text-xs font-medium text-tertiary'>
                Example: Set custom properties with overwrite mode
              </p>
              <Button
                color='secondary'
                size='sm'
                iconLeading={
                  copiedTarget === 'payload-properties' ? Check : Copy01
                }
                onClick={handleCopyPayloadProperties}
                title='Copy payload'
              />
            </div>
            <pre className='bg-bg-secondary rounded-lg px-4 py-3 border border-border-secondary text-xs font-mono text-tertiary overflow-x-auto whitespace-pre'>
              {PAYLOAD_EXAMPLE_PROPERTIES}
            </pre>
          </div>

          {/* Response example */}
          <div>
            <div className='flex items-center justify-between mb-1.5'>
              <p className='text-xs font-medium text-tertiary'>
                Example success response
              </p>
              <Button
                color='secondary'
                size='sm'
                iconLeading={copiedTarget === 'response' ? Check : Copy01}
                onClick={handleCopyResponse}
                title='Copy response'
              />
            </div>
            <pre className='bg-bg-secondary rounded-lg px-4 py-3 border border-border-secondary text-xs font-mono text-tertiary overflow-x-auto whitespace-pre'>
              {RESPONSE_EXAMPLE}
            </pre>
          </div>

          {/* Validation error codes reference */}
          <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
            <h4 className='text-sm font-semibold text-secondary mb-3'>
              Validation error codes
            </h4>
            <p className='text-xs text-tertiary mb-3'>
              Validation failures return HTTP 400 with{' '}
              <code className='bg-bg-primary px-1 rounded'>
                error_type: &quot;validation_error&quot;
              </code>{' '}
              and a{' '}
              <code className='bg-bg-primary px-1 rounded'>
                validation_errors
              </code>{' '}
              array. Each entry includes a{' '}
              <code className='bg-bg-primary px-1 rounded'>code</code> field
              identifying the problem.
            </p>
            <ul className='list-disc list-inside space-y-2 ml-2 text-xs text-tertiary'>
              <li>
                <code className='bg-bg-primary px-1 rounded'>
                  field_collision
                </code>{' '}
                – a{' '}
                <code className='bg-bg-primary px-1 rounded'>contact.*</code>{' '}
                shortcut and the corresponding{' '}
                <code className='bg-bg-primary px-1 rounded'>properties.*</code>{' '}
                entry were both provided
              </li>
              <li>
                <code className='bg-bg-primary px-1 rounded'>
                  unknown_property
                </code>{' '}
                – a property name in{' '}
                <code className='bg-bg-primary px-1 rounded'>properties</code>{' '}
                is not registered on{' '}
                <code className='bg-bg-primary px-1 rounded'>ahoy_contact</code>
              </li>
              <li>
                <code className='bg-bg-primary px-1 rounded'>
                  readonly_property
                </code>{' '}
                – a property has{' '}
                <code className='bg-bg-primary px-1 rounded'>
                  value_write_access
                </code>{' '}
                other than{' '}
                <code className='bg-bg-primary px-1 rounded'>
                  &quot;all&quot;
                </code>
              </li>
              <li>
                <code className='bg-bg-primary px-1 rounded'>
                  invalid_write_mode
                </code>{' '}
                – <code className='bg-bg-primary px-1 rounded'>write_mode</code>{' '}
                is not{' '}
                <code className='bg-bg-primary px-1 rounded'>
                  &quot;update_if_empty&quot;
                </code>{' '}
                or{' '}
                <code className='bg-bg-primary px-1 rounded'>
                  &quot;overwrite&quot;
                </code>
              </li>
              <li>
                <code className='bg-bg-primary px-1 rounded'>
                  properties_limit_exceeded
                </code>{' '}
                – <code className='bg-bg-primary px-1 rounded'>properties</code>{' '}
                contains more than 200 keys
              </li>
            </ul>
          </div>

          {/* Error response example */}
          <div>
            <div className='flex items-center justify-between mb-1.5'>
              <p className='text-xs font-medium text-tertiary'>
                Example error response
              </p>
              <Button
                color='secondary'
                size='sm'
                iconLeading={copiedTarget === 'error' ? Check : Copy01}
                onClick={handleCopyErrorResponse}
                title='Copy error response'
              />
            </div>
            <pre className='bg-bg-secondary rounded-lg px-4 py-3 border border-border-secondary text-xs font-mono text-tertiary overflow-x-auto whitespace-pre'>
              {ERROR_RESPONSE_EXAMPLE}
            </pre>
          </div>
        </section>

        {/* Delete confirmation modal — sits inside AccordionPanel so the
          modal is mounted whenever the disclosure is. The modal itself
          portals to the document root, so its layout doesn't affect
          the accordion's inner content sizing. */}
        <ConfirmationDialog
          isOpen={!!keyToDelete}
          title='Delete API key?'
          description={
            keyToDelete
              ? `"${keyToDelete.name}" will be permanently deleted. Any integrations using this key will stop working.`
              : undefined
          }
          confirmLabel='Delete'
          variant='destructive'
          isLoading={deleteKeyMutation.isPending}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      </AccordionPanel>
    </AccordionItem>
  );
}
